/**
 * LegalPage.tsx — Ruta /legal de PTEL Tool
 *
 * Tres documentos legales en borrador con banner de «Versión provisional».
 * Tabs: Aviso Legal · Privacidad · Cookies
 *
 * @version 1.1.0
 * @task F2.01 — Plan de Trabajo Legal
 * @change v1.1: sin emojis, sin copyright GREA, tabs minimalistas inline
 */

import React, { useState } from 'react';
import { useAppContext } from '../../contexts/AppContext';
import './LegalPage.css';

// ─── Tipos ────────────────────────────────────────────────────────────────────

type TabId = 'aviso' | 'privacidad' | 'cookies';

interface Tab {
  id: TabId;
  label: string;
}

// ─── Constantes ───────────────────────────────────────────────────────────────

const TABS: Tab[] = [
  { id: 'aviso',      label: 'Aviso Legal'            },
  { id: 'privacidad', label: 'Política de Privacidad' },
  { id: 'cookies',    label: 'Cookies'                },
];

// ─── Subcomponentes ───────────────────────────────────────────────────────────

/** Resalta los placeholders [entre corchetes] en ámbar */
function Placeholder({ children }: { children: string }) {
  return <mark className="legal-placeholder">{children}</mark>;
}

/** Celda de tabla con posible placeholder */
function Td({ children }: { children: string }) {
  const isPlaceholder = children.startsWith('[') && children.endsWith(']');
  return (
    <td>
      {isPlaceholder ? <Placeholder>{children}</Placeholder> : children}
    </td>
  );
}

// ─── Contenido: Aviso Legal ───────────────────────────────────────────────────

function AvisoLegal() {
  return (
    <article className="legal-doc">
      <header className="legal-doc-header">
        <h2>Aviso Legal y Condiciones de Uso</h2>
        <p className="legal-version">Versión 1.0 · Borrador para revisión jurídica</p>
      </header>

      <section>
        <h3>1. Identificación del titular</h3>
        <table className="legal-table">
          <tbody>
            <tr><th>Denominación</th><Td>[Denominación completa del organismo: GREA / Consejería / Agencia]</Td></tr>
            <tr><th>NIF / CIF</th><Td>[NIF del organismo responsable]</Td></tr>
            <tr><th>Dirección</th><Td>[Dirección postal completa]</Td></tr>
            <tr><th>Correo electrónico</th><Td>[email de contacto institucional]</Td></tr>
            <tr><th>Teléfono</th><Td>[Teléfono de contacto]</Td></tr>
            <tr><th>Delegado de Protección de Datos</th><Td>[Nombre y email del DPD de la Junta de Andalucía o del organismo]</Td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h3>2. Objeto y ámbito de aplicación</h3>
        <p>PTEL Tool es una aplicación web de apoyo técnico para la normalización, geocodificación y validación de coordenadas de infraestructuras críticas contenidas en los Planes Territoriales de Emergencias Locales (PTEL) de los municipios de Andalucía, en el marco de la Ley 2/2002, de 11 de noviembre, de Gestión de Emergencias en Andalucía, y del Decreto 197/2024, de 3 de septiembre, por el que se regula el Sistema Local de Protección Civil en los municipios de la Comunidad Autónoma de Andalucía.</p>
        <p>El acceso y uso de PTEL Tool implica la aceptación plena y sin reservas de las presentes condiciones de uso. Si no estás de acuerdo con alguna de ellas, debes abstenerte de utilizar la herramienta.</p>
      </section>

      <section>
        <h3>3. Condiciones de uso y usuarios autorizados</h3>
        <p>El acceso a PTEL Tool está restringido a:</p>
        <ul>
          <li>Personal técnico de GREA y de las consejerías competentes en materia de emergencias de la Junta de Andalucía.</li>
          <li>Técnicos municipales de los 785 municipios de Andalucía autorizados por el administrador del sistema.</li>
          <li>Colaboradores externos expresamente autorizados por GREA para el desarrollo y mantenimiento de la herramienta.</li>
        </ul>
        <p>El acceso no autorizado o la utilización de la herramienta por personas no incluidas en las categorías anteriores queda expresamente prohibido.</p>
      </section>

      <section>
        <h3>4. Uso correcto y usos prohibidos</h3>
        <p>El usuario se compromete a utilizar PTEL Tool exclusivamente para las finalidades para las que fue diseñada: normalización y validación de coordenadas de infraestructuras en documentos PTEL, en el ejercicio de sus funciones profesionales.</p>
        <p>Queda expresamente prohibido:</p>
        <ul>
          <li>Utilizar la herramienta para finalidades distintas de la gestión de PTELs.</li>
          <li>Compartir credenciales de acceso con terceros no autorizados.</li>
          <li>Intentar acceder a funcionalidades o datos para los que no se disponga de autorización.</li>
          <li>Cargar documentos que no sean PTELs municipales o documentos relacionados con la gestión de emergencias.</li>
          <li>Realizar cualquier acción que pueda comprometer la seguridad, disponibilidad o integridad del sistema.</li>
        </ul>
      </section>

      <section>
        <h3>5. Propiedad intelectual</h3>
        <p>El código fuente de PTEL Tool está disponible en el repositorio GitHub. Su uso, reproducción o distribución requiere autorización expresa del autor. Para solicitudes de acceso técnico o institucional, contacta a través del correo de contacto del proyecto. Los datos de referencia utilizados (CDAU, NGA, DERA, Catastro, CartoCiudad, AGOL) son propiedad de sus respectivos organismos y se utilizan bajo las licencias CC BY 4.0 e INSPIRE. Consulta la página de <a href="#/transparencia">Transparencia</a> para el detalle completo de las atribuciones.</p>
      </section>

      <section>
        <h3>6. Exención de responsabilidad</h3>
        <p>PTEL Tool es una herramienta de apoyo técnico. Los resultados de normalización y geocodificación que proporciona son orientativos y deben ser verificados por el técnico responsable antes de su incorporación a cualquier documento oficial. <Placeholder>[El organismo titular]</Placeholder> no asume responsabilidad alguna por el uso incorrecto de los resultados obtenidos.</p>
        <p>El servicio se presta en régimen de disponibilidad continua pero sin garantía de disponibilidad 24/7. <Placeholder>[El organismo titular]</Placeholder> se reserva el derecho de interrumpir el servicio para mantenimiento, actualizaciones o por razones de seguridad, sin previo aviso.</p>
      </section>

      <section>
        <h3>7. Legislación aplicable y jurisdicción</h3>
        <p>Las presentes condiciones de uso se rigen por la legislación española. Para cualquier controversia derivada del uso de PTEL Tool, las partes se someten a la jurisdicción de los Juzgados y Tribunales de [Sevilla — verificar con jurídico según sede del organismo titular], con renuncia expresa a cualquier otro fuero que pudiera corresponderles.</p>
      </section>
    </article>
  );
}

// ─── Contenido: Política de Privacidad ───────────────────────────────────────

function Privacidad() {
  return (
    <article className="legal-doc">
      <header className="legal-doc-header">
        <h2>Política de Privacidad</h2>
        <p className="legal-version">
          Versión 1.0 · Borrador para revisión jurídica ·
          Aplicable desde: <Placeholder>[fecha de publicación]</Placeholder>
        </p>
      </header>

      <div className="legal-summary">
        <strong>Resumen</strong>
        <p>PTEL Tool recoge únicamente nombre, apellidos y correo electrónico de sus usuarios registrados, a través de Firebase Authentication (Google). No procesa datos de ciudadanos. Trabaja exclusivamente con datos de infraestructuras públicas (hospitales, colegios, parques de bomberos, etc.). El contenido de los documentos PTEL se analiza localmente en el navegador del técnico. Los datos de infraestructuras públicas resultantes (coordenadas normalizadas, tipología, municipio) se almacenan en la infraestructura propia del GREA en la Unión Europea.</p>
      </div>

      <section>
        <h3>1. Responsable del tratamiento</h3>
        <table className="legal-table">
          <tbody>
            <tr><th>Responsable</th><Td>[Denominación del organismo responsable]</Td></tr>
            <tr><th>NIF</th><Td>[NIF]</Td></tr>
            <tr><th>Dirección</th><Td>[Dirección postal]</Td></tr>
            <tr><th>Email de contacto</th><Td>[email@organismo.junta-andalucia.es]</Td></tr>
            <tr><th>DPD</th><Td>[Nombre y email del DPD]</Td></tr>
          </tbody>
        </table>
      </section>

      <section>
        <h3>2. Encargados del tratamiento (proveedores externos)</h3>
        <div className="legal-table-scroll">
          <table className="legal-table legal-table--wide">
            <thead>
              <tr><th>Proveedor</th><th>Servicio</th><th>País</th><th>Base legal transferencia</th></tr>
            </thead>
            <tbody>
              <tr><td>Firebase / Google</td><td>Autenticación de usuarios</td><td>UE (Frankfurt)</td><td>Google Cloud DPA + Cláusulas Contractuales Tipo</td></tr>
              <tr><td>Cloudflare R2</td><td>Servicio estático de datos geográficos (CDAU, NGA, DERA)</td><td>UE</td><td>Cloudflare DPA</td></tr>
              <tr><td>GitHub Pages</td><td>Hospedaje de la aplicación web</td><td>EE.UU. / UE</td><td>GitHub DPA + Cláusulas Contractuales Tipo</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h3>3. Categorías de datos tratados y finalidades</h3>
        <div className="legal-table-scroll">
          <table className="legal-table legal-table--wide">
            <thead>
              <tr><th>Categoría</th><th>Finalidad</th><th>Base legal</th><th>Plazo de conservación</th></tr>
            </thead>
            <tbody>
              <tr>
                <td>Nombre y apellidos</td>
                <td>Identificación del usuario en el sistema</td>
                <td>Ejecución de servicio / misión de interés público (art. 6.1.b y 6.1.e RGPD)</td>
                <td>Mientras la cuenta esté activa + 3 años tras baja</td>
              </tr>
              <tr>
                <td>Dirección de correo electrónico</td>
                <td>Autenticación, notificaciones y recuperación de contraseña</td>
                <td>Ejecución de servicio / misión de interés público</td>
                <td>Mientras la cuenta esté activa + 3 años tras baja</td>
              </tr>
              <tr>
                <td>Registros de autenticación (Firebase)</td>
                <td>Seguridad y auditoría del sistema</td>
                <td>Interés legítimo (art. 6.1.f RGPD)</td>
                <td>90 días</td>
              </tr>
            </tbody>
          </table>
        </div>
        <p>PTEL Tool no trata datos personales de ciudadanos. Los documentos PTEL contienen datos de infraestructuras públicas (hospitales, colegios, parques de bomberos, etc.). El contenido íntegro de los documentos no se transmite a ningún servidor. Los datos de infraestructuras resultantes (coordenadas normalizadas, tipología, municipio) pueden almacenarse en la infraestructura propia del proyecto alojada en la Unión Europea, con base legal en la misión de interés público vinculada al Decreto 197/2024.</p>
      </section>

      <section>
        <h3>4. Derechos de las personas interesadas</h3>
        <p>Las personas cuyos datos son tratados por PTEL Tool tienen derecho a: acceso, rectificación, supresión, limitación del tratamiento, portabilidad y oposición.</p>
        <p>Para ejercer cualquiera de estos derechos, puede dirigirse a <Placeholder>[email del DPD o del responsable]</Placeholder> indicando en el asunto «Ejercicio de derechos RGPD — PTEL Tool». Si no recibe respuesta satisfactoria en el plazo de un mes, puede presentar una reclamación ante la Agencia Española de Protección de Datos (<a href="https://www.aepd.es" target="_blank" rel="noreferrer">www.aepd.es</a>).</p>
      </section>

      <section>
        <h3>5. Medidas de seguridad</h3>
        <ul>
          <li>Autenticación mediante Firebase Authentication con verificación de correo electrónico.</li>
          <li>Comunicaciones cifradas mediante HTTPS/TLS en todas las transmisiones.</li>
          <li>Almacenamiento de contraseñas mediante hash seguro (gestionado por Firebase).</li>
          <li>Control de acceso por roles (usuario, editor, administrador).</li>
        </ul>
      </section>

      <section>
        <h3>6. Modificaciones de esta política</h3>
        <p>Esta política puede ser actualizada para adaptarse a cambios normativos o nuevas funcionalidades. Las modificaciones sustanciales serán comunicadas a los usuarios registrados por correo electrónico con al menos 15 días de antelación a su entrada en vigor.</p>
      </section>
    </article>
  );
}

// ─── Contenido: Cookies ───────────────────────────────────────────────────────

function Cookies() {
  return (
    <article className="legal-doc">
      <header className="legal-doc-header">
        <h2>Política de Cookies y Almacenamiento Local</h2>
        <p className="legal-version">Versión 1.0 · Borrador para revisión jurídica</p>
      </header>

      <section>
        <h3>1. Qué es una cookie</h3>
        <p>Una cookie es un pequeño archivo de texto que un sitio web almacena en el dispositivo del usuario cuando lo visita. Las cookies permiten que el sitio recuerde información entre visitas, como el estado de autenticación.</p>
        <p>Además de las cookies tradicionales, las aplicaciones web modernas utilizan otros mecanismos de almacenamiento en el navegador (localStorage, sessionStorage, IndexedDB) con funciones similares o complementarias.</p>
      </section>

      <section>
        <h3>2. Clasificación por obligación de consentimiento</h3>
        <div className="legal-table-scroll">
          <table className="legal-table legal-table--wide">
            <thead>
              <tr><th>Tipo</th><th>Descripción</th><th>Requiere consentimiento</th></tr>
            </thead>
            <tbody>
              <tr><td><strong>Técnicas / estrictamente necesarias</strong></td><td>Imprescindibles para el funcionamiento básico del servicio.</td><td className="legal-td-no">No (exentas)</td></tr>
              <tr><td><strong>Preferencias / funcionales</strong></td><td>Recuerdan configuraciones del usuario (tema, idioma).</td><td className="legal-td-cond">Sí (salvo estrictamente necesarias)</td></tr>
              <tr><td><strong>Analíticas / estadísticas</strong></td><td>Miden el uso del servicio para mejorarlo.</td><td className="legal-td-yes">Sí</td></tr>
              <tr><td><strong>Marketing / publicidad</strong></td><td>PTEL Tool no utiliza cookies de este tipo.</td><td className="legal-td-na">N/A</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <section>
        <h3>3. Cookies y almacenamiento utilizados en PTEL Tool</h3>
        <div className="legal-table-scroll">
          <table className="legal-table legal-table--wide">
            <thead>
              <tr><th>Identificador</th><th>Tipo</th><th>Origen</th><th>Finalidad</th><th>Duración</th></tr>
            </thead>
            <tbody>
              <tr><td><code>firebase:authUser:[API_KEY]</code></td><td>Técnica</td><td>Firebase / Google</td><td>Mantener la sesión del usuario autenticado entre recargas</td><td>Hasta cierre de sesión</td></tr>
              <tr><td><code>localStorage: ptel-theme</code></td><td>Preferencia funcional</td><td>PTEL Tool</td><td>Recordar la preferencia de tema claro/oscuro</td><td>Persistente hasta borrado manual</td></tr>
              <tr><td><code>localStorage: geocodingCache-*</code></td><td>Técnica (funcional)</td><td>PTEL Tool</td><td>Caché local de resultados de geocodificación</td><td>TTL 90 días</td></tr>
              <tr><td><code>IndexedDB: ptel-geodata</code></td><td>Técnica (funcional)</td><td>PTEL Tool</td><td>Datasets de referencia geográfica (CDAU, NGA) para funcionamiento offline</td><td>Hasta borrado manual</td></tr>
              <tr><td><code>sessionStorage: scrollTo</code></td><td>Técnica</td><td>PTEL Tool</td><td>Preservar posición de scroll entre páginas. Se elimina al cerrar la pestaña.</td><td>Duración de la sesión</td></tr>
            </tbody>
          </table>
        </div>
      </section>

      <div className="legal-banner legal-banner--info">
        <strong>Conclusión: no es necesario banner de consentimiento en la configuración actual</strong>
        <p>Las cookies y el almacenamiento local de PTEL Tool son técnicas o de preferencia funcional. Ninguna requiere consentimiento previo según la normativa AEPD vigente, <strong>siempre que Google Analytics no esté activo</strong>. Si se activa Google Analytics, será necesario implementar un banner de consentimiento antes de cargar el script.</p>
      </div>

      <section>
        <h3>4. Cómo gestionar o eliminar las cookies</h3>
        <p>Puedes eliminar las cookies y el almacenamiento local de PTEL Tool desde la configuración de tu navegador: en Chrome (Configuración → Privacidad y seguridad), Firefox (Configuración → Privacidad y seguridad), Safari (Preferencias → Privacidad) o Edge (Configuración → Privacidad, búsqueda y servicios).</p>
        <p>La eliminación de la cookie de sesión de Firebase cerrará tu sesión en PTEL Tool. La eliminación de la caché de geocodificación solo afectará al rendimiento y se regenerará automáticamente en el siguiente uso.</p>
      </section>

      <section>
        <h3>5. Actualización de esta política</h3>
        <p>Esta política se actualizará cuando se activen nuevas funcionalidades que introduzcan nuevos mecanismos de almacenamiento, especialmente las previstas en las Fases G, R y C del roadmap.</p>
      </section>
    </article>
  );
}

// ─── Componente principal ─────────────────────────────────────────────────────

export function LegalPage() {
  const { theme } = useAppContext();
  const [activeTab, setActiveTab] = useState<TabId>('aviso');

  return (
    <div className="legal-page" data-theme={theme}>

      {/* ── Banner de borrador ── */}
      <div className="legal-draft-banner" role="alert">
        <strong>Versión provisional — en revisión jurídica</strong>
        <span>Borrador elaborado con asistencia de IA. Sin validez legal hasta su revisión y aprobación por el servicio jurídico de GREA / Junta de Andalucía.</span>
      </div>

      {/* ── Cabecera ── */}
      <header className="legal-header">
        <div className="legal-header-inner">
          <p className="legal-header-eyebrow">PTEL Tool · Documentación Legal</p>
          <h1>Marco Legal de la Aplicación</h1>
          <p className="legal-header-sub">
            Versión 1.0 · Marzo 2026 · GREA — Grupo de Emergencias de Andalucía
          </p>

          {/* ── Tabs inline minimalistas ── */}
          <nav className="legal-tabs" role="tablist" aria-label="Documentos legales">
            {TABS.map((tab, i) => (
              <React.Fragment key={tab.id}>
                {i > 0 && <span className="legal-tab-sep" aria-hidden="true">·</span>}
                <button
                  role="tab"
                  aria-selected={activeTab === tab.id}
                  aria-controls={`legal-panel-${tab.id}`}
                  className={`legal-tab ${activeTab === tab.id ? 'legal-tab--active' : ''}`}
                  onClick={() => setActiveTab(tab.id)}
                >
                  {tab.label}
                </button>
              </React.Fragment>
            ))}
          </nav>
        </div>
      </header>

      {/* ── Contenido ── */}
      <main className="legal-main">
        <div id="legal-panel-aviso" role="tabpanel" hidden={activeTab !== 'aviso'}>
          {activeTab === 'aviso' && <AvisoLegal />}
        </div>
        <div id="legal-panel-privacidad" role="tabpanel" hidden={activeTab !== 'privacidad'}>
          {activeTab === 'privacidad' && <Privacidad />}
        </div>
        <div id="legal-panel-cookies" role="tabpanel" hidden={activeTab !== 'cookies'}>
          {activeTab === 'cookies' && <Cookies />}
        </div>
      </main>

      {/* ── Pie ── */}
      <footer className="legal-footer">
        <p>Elaborado con asistencia de IA (Claude, Anthropic) · Normativa española y europea vigente en marzo de 2026.</p>
      </footer>
    </div>
  );
}

export default LegalPage;
